import { createGlobalStyle, css } from "styled-components"
import "./css/font.css"
import "./css/bootstrap-icons.css"

const RootStyle = css`
    :root {
        --base-light-color: rgb(252, 252, 252);
        --base-color: #f9f7f7;
        --base-dark-color: rgb(231, 225, 225);

        --main-light-color: rgb(168, 223, 255);
        --main-color: rgb(112, 188, 232);
        --main-dark-color: rgb(68, 150, 198);
        --main-point-color: #0372ff;

        --sub-light-color: rgb(244, 248, 255);
        --sub-color: #dbe2ef;
        --sub-dark-color: rgb(186, 195, 211);

        --deep-light-color: rgb(114, 151, 199);
        --deep-color: rgb(63, 119, 175);
        --deep-dark-color: rgb(46, 95, 140);

        --highlight-light-color: rgb(255, 233, 167);
        --highlight-color: rgb(255, 207, 104);
        --highlight-dark-color: rgb(255, 189, 34);

        --dark-color: #333333;
        --darkgray-color: #555555;
        --gray-color: #666666;
        --gray3-color: #999999;
        --lightgray-color: #bbbbbb;
        --light-color: #eeeeee;
        --white-color: #ffffff;
        --black-color: #000000;

        --text-color: var(--black-color);
        --bg-light: #f6f7f9;
        --bg-dark: #eaebec;
        --bg-blue: #dbebff;
        --bg-green: #daf8e1;
        --green-dark: #00b52a;
        --divider-dark: #e5e5e5;
        --disabled: #b9c2cd;
    }
`

const CommonStyle = css`
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        font-weight: 700;
        line-height: 1.3em;
    }
    h1 {
        font-size: 4.8rem;
    }
    h2 {
        font-weight: 700;
        font-size: 32px;
    }
    h3 {
        font-size: 3.2rem;
    }
    h4 {
        font-weight: 600;
        font-size: 24px;
    }
    h5 {
        font-weight: 500;
        font-size: 16px;
    }
    h6 {
        font-size: 2rem;
    }
    p {
        font-weight: 500;
    }
    b {
        font-weight: 700;
    }
    small {
        font-size: 1.2rem;
        font-weight: 300;
    }
`

const GlobalStyle = createGlobalStyle`
    /* color default value */
    ${RootStyle}

    /* reset */
    * {
        box-sizing: border-box;
    }

    html, body, div, span, applet, object, iframe,
    h1, h2, h3, h4, h5, h6, p, blockquote, pre,
    a, abbr, acronym, address, big, cite, code,
    del, dfn, em, img, ins, kbd, q, s, samp,
    small, strike, strong, sub, sup, tt, var,
    b, u, i, center, dl, dt, dd, ol, ul, li,
    fieldset, form, label, legend,
    table, caption, tbody, tfoot, thead, tr, th, td,
    article, aside, canvas, details, embed, 
    figure, figcaption, footer, header, hgroup, 
    menu, nav, output, ruby, section, summary,
    time, mark, audio, video {
        margin: 0;
        padding: 0;
        font-size: 100%;
        font: inherit;
        vertical-align: baseline;
    }

    html, body {
        width: 100%;
        padding: 0;
        margin: 0;
        font-size: 62.5%;
        scroll-behavior: smooth;
    }

    article, aside, details, figcaption, figure, 
    footer, header, hgroup, menu, nav, section {
        display: block;
    }
    body { line-height: 1; }
    ol, ul { list-style: none; }
    blockquote, q { quotes: none; }
    blockquote:before, blockquote:after,
    q:before, q:after {
        content: '';
        content: none;
    }
    table {
        border-collapse: collapse;
        border-spacing: 0;
    }

    /* 기타 설정 */
    body, a, button, input, select, textarea {
        font-family: 'Pretendard', 'Gotham';
        font-weight: 500;
        font-size: 1.4rem;
        letter-spacing: -0.03em;
        color: var(--text-color);
    }

    a, button {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        text-decoration: none;
    }
    
    a {
        display: inline;
    }

    input[type="checkbox"], input[type="submit"],
    button, a, input ~ label {
        cursor: pointer;
    }

    button:disabled, a:disabled, input ~ label:disabled {
        cursor: not-allowed;
    }

    ::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }
    ::-webkit-scrollbar-track {
        background: var(--base-light-color);
    }
    ::-webkit-scrollbar-thumb {
        background-color: var(--deep-dark-color);
        border-radius: 4px;
        border: 2px solid var(--sub-light-color);
    }
    ::-webkit-scrollbar-thumb:hover {
        background-color: var(--dark-color);
    }

    .bi {
        display: inline-block;
        font-family: 'bootstrap-icons' !important;
        font-style: normal;
        font-weight: 400;
        line-height: 1;
        text-transform: none;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
     }

    .blind {
        position: absolute;
        width: 0;
        height: 0;
        font-size: 0;
        line-height: 0;
        overflow: hidden;
    }

    /* 커스텀 모달 (alert, confirm) */
    .custom-modal .modal-content {
        font-weight: 300;
        background-color: var(--base-color);
        border: 1px solid var(--sub-dark-color);
        border-radius: 12px;
        box-shadow: 0 8px 16px rgba(0,0,0,0.1);
        padding: 1rem;
    }
    .custom-modal .modal-body {
        color: var(--text-color);
        font-size: 1.6rem;
        padding: 1rem 1.5rem;
        line-height: 1.4;
        text-align: center;
        letter-spacing: 0.1px;
        white-space: pre-line;
    }
    .custom-modal .modal-footer {
        border-top: none;
        padding: 1rem 1.5rem 0.75rem 1.5rem;
        justify-content: center;
    }
    .custom-modal .confirm-btn {
        background-color: var(--main-color);
        border-color: var(--main-dark-color);
        color: #fff;
        padding: 0.5rem 1.25rem;
        border-radius: 4px;
    }
    .custom-modal .confirm-btn:hover {
        background-color: var(--main-dark-color);
        border-color: var(--main-dark-color);
    }
    .custom-modal .btn {
        font-weight: 400;
        font-size: 1.5rem;
        letter-spacing: 0.05em;
        box-shadow: none;
    }
    
    .hover-row:hover td {
        cursor: pointer;
        color: var(--main-dark-color);
    }

    .empty-row {
        td {
            color: var(--gray-color);
            text-align: center;
            height: 60px;
            font-size: 1.2em;
            background-color: var(--base-color)
        }
    }

    .review-card {
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding: 32px;
        background: var(--white-color);
        border: 1px solid #ededed;
        border-radius: 16px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }

    .form-card {
        padding: 30px;
        border: 1px solid var(--sub-color);
        border-bottom: 1px solid var(--sub-dark-color);
        border-radius: 8px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
        box-shadow: 0px 0px 48px 0px rgba(0, 0, 0, 0.08);
    }

    .title-desc {
        font-weight: 500;
        font-size: 16px;
        line-height: 1.5em;
        color: var(--gray-color);
    }

    /* 공통 스타일 */
    ${CommonStyle}
`

export default GlobalStyle
